<script lang="ts" setup>
import type { ITestimonial } from '@/types/LP/testimonial';

import { register } from 'swiper/element/bundle';
const { locale, t } = useI18n()
const { $api } = useNuxtApp()

interface ILocaleString {
  en: string;
  id: string;
}

interface ISectionData {
  section_title: ILocaleString;
}

const sectionData: ISectionData = {
    "section_title": {
        "en": "Job Seeker Reviews",
        "id": "Ulasan Pencari Kerja"
    }
}

const sectionTitle = computed(() => {
  const lang = locale.value as 'en' | 'id'
  return sectionData.section_title[lang]
})


const testimonial = ref<ITestimonial[]>([])
const isSwiperBeginning = ref(false)
const isSwiperEnd = ref(false)
const isLoading = ref(true)

const initSwiper = () => {
    const swiperEl = document.querySelector('.reviews__slider-wraper') as any
    if (!swiperEl) return

    const params = {
        slidesPerView: 1,
        spaceBetween: 10,
        a11y: false,
        navigation: {
            prevEl: '.reviews__button-prev',
            nextEl: '.reviews__button-next',
        },
        pagination: {
            el: '.reviews__pagination',
            clickable: true,
        },

        breakpoints: {
            760: {
                slidesPerView: 2,
                spaceBetween: 30
            }
        },
       injectStyles: [
          `.swiper-button-next, .swiper-button-prev { display: none; }`,
          
          /* --- Perbaikan untuk Pagination sesuai Gambar --- */
        ],
    }

    Object.assign(swiperEl, params)
    swiperEl.initialize()
}

onMounted(() => {
    register() // Register the Web Component
    
    nextTick(async () => {
        isLoading.value = true
        try {
            const { data: contentData } = await $api.landingPage.getLpCustomerReviewNew()
            testimonial.value = contentData.value.data
        } finally {
            isLoading.value = false
            
            // Wait for Vue to render the v-else block, then init swiper
            setTimeout(() => {
                initSwiper()
            }, 50)
        }
    })
})

const onProgress = (event: any) => {
    const [swiper] = event.detail
    isSwiperBeginning.value = swiper.isBeginning
    isSwiperEnd.value = swiper.isEnd
}
</script>

<template>
    <section v-if="isLoading || testimonial.length > 0" class="reviews">
        <svg class="reviews__art animate-shape" width="300" height="196" viewBox="0 0 300 196" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M-15.071 204C-13.0269 172.45 0.139867 142.904 21.7709 121.328C43.402 99.752 71.8861 87.7525 101.472 87.7525C131.058 87.7525 159.542 99.752 181.173 121.328C202.804 142.904 215.971 172.45 218.015 204L300 204C297.982 149.17 276.186 97.303 239.181 59.2723C202.175 21.2417 152.834 1.67658e-05 101.5 1.19689e-05C50.1658 7.17201e-06 0.824508 21.2417 -36.1808 59.2723C-73.1861 97.303 -94.9822 149.17 -97 204L-15.071 204Z" fill="#FF1D25"/>
        </svg>
         <VContainer>
            <div class="reviews__inner">
                <div class="reviews__header">
                     <SectionTitle 
                        size="md"
                        align="left"
                        :title="sectionTitle"
                    />
                    <div class="reviews__navigation">
                        <VBtn
                            class="reviews__button-prev"
                            icon="ri-arrow-left-s-line"
                            color="error"
                            :aria-label="t('previous_slide')"
                            :disabled="isSwiperBeginning"
                        ></VBtn>
                        <VBtn
                            class="reviews__button-next"
                            icon="ri-arrow-right-s-line"
                            color="error"
                            :aria-label="t('next_slide')"
                            :disabled="isSwiperEnd"
                        ></VBtn>
                    </div>
                </div>
                <div class="reviews__content">
                     <VRow v-if="isLoading">
                        <VCol v-for="index in 2" :key="index" cols="12" md="6">
                            <CardReviewSkeleton />
                        </VCol>
                    </VRow>
                    <swiper-container
                        class="reviews__slider-wraper"
                        v-else
                        init="false"
                        @swiperprogress="onProgress"
                    >
                        <swiper-slide class="reviews__slide"
                            v-for="(review, index) in testimonial"
                            :key="index"
                        >
                            <CardReview :review="review" />
                        </swiper-slide>
                    </swiper-container>
                    <div class="reviews__pagination"></div>
                </div>
            </div>
         </VContainer>
    </section>
</template>

<style lang="scss" scoped>
   @import "swiper/swiper-bundle.css"; 
    .reviews{
        @include vwUnit('padding', 80 0 80, 42 0 42);
        position: relative;
        background-color: #FFF4F4;
        overflow: hidden;


        &__art{
            position: absolute;
            bottom: 0;
            left: 0;
            z-index: 0;
            @include vwUnit('max-width', 397, 200);
            height: auto;
        }

        &__header{
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        &__navigation{
            display: flex;
            justify-content: space-between;
            @include vwUnit('gap', 20, 20);
        }

        &__content{
            position: relative;
            z-index: 10;
        }

        &__slide{
            @include vwUnit('padding', 30 0, 24 0);
        }

        &__pagination {
            justify-content: center;
            align-items: center;
            margin-top: 20px;
            display: none;
            @media only screen and (max-width: 991.98px) {
                 display: flex;
            }
            
            :deep(.swiper-pagination-bullet) {
                width: 8px;
                height: 8px;
                background-color: #211F1F;
                opacity: 0.3;
                transition: all 0.3s ease;
                border-radius: 50%;
                cursor: pointer;
                margin-left: 4px;
                margin-right: 4px;
            }

            :deep(.swiper-pagination-bullet-active) {
                opacity: 1;
                width: 24px; 
                border-radius: 5px;
            }
           
           
        }
    }
</style>
